/**
 * Keyboard mapping of the game and the rules for keys the game must leave alone.
 *
 * Space / ArrowUp / W jump, 1 / 2 use a boost slot, Escape opens the in-game menu, M mutes
 * the microphone, ArrowLeft / ArrowRight switch the watched racer (spectators). Physical key
 * codes are used, so the keys work on a Russian layout too; `key` is the fallback for
 * keyboards that report no code (some on-screen keyboards).
 */
export type KeyCommand =
  | { kind: 'jump' }
  | { kind: 'boost'; slot: number }
  | { kind: 'menu' }
  | { kind: 'mute' }
  | { kind: 'switch'; step: -1 | 1 };

const JUMP: KeyCommand = { kind: 'jump' };
const MENU: KeyCommand = { kind: 'menu' };
const MUTE: KeyCommand = { kind: 'mute' };
const BOOST_1: KeyCommand = { kind: 'boost', slot: 0 };
const BOOST_2: KeyCommand = { kind: 'boost', slot: 1 };
const PREVIOUS: KeyCommand = { kind: 'switch', step: -1 };
const NEXT: KeyCommand = { kind: 'switch', step: 1 };

const BY_CODE: Readonly<Record<string, KeyCommand>> = {
  Space: JUMP,
  ArrowUp: JUMP,
  KeyW: JUMP,
  Digit1: BOOST_1,
  Numpad1: BOOST_1,
  Digit2: BOOST_2,
  Numpad2: BOOST_2,
  Escape: MENU,
  KeyM: MUTE,
  ArrowLeft: PREVIOUS,
  ArrowRight: NEXT,
};

const BY_KEY: Readonly<Record<string, KeyCommand>> = {
  ' ': JUMP,
  ArrowUp: JUMP,
  w: JUMP,
  W: JUMP,
  '1': BOOST_1,
  '2': BOOST_2,
  Escape: MENU,
  m: MUTE,
  M: MUTE,
  ArrowLeft: PREVIOUS,
  ArrowRight: NEXT,
};

export function keyCommand(code: string, key: string): KeyCommand | null {
  if (code) return BY_CODE[code] ?? null;
  return BY_KEY[key] ?? null;
}

/** Text fields keep their keys (Space types a space, M an "m"). */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select')) return true;
  return target instanceof HTMLElement && target.isContentEditable;
}

/**
 * Keys the game must ignore: shortcuts with Ctrl / Cmd / Alt, typing in a text field, and
 * everything while one of the shell's dialogs is open (see behindShellDialog).
 */
export function ignoreKeyEvent(event: KeyboardEvent, gameRoot: Element | null): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return true;
  if (isTypingTarget(event.target)) return true;
  return behindShellDialog(gameRoot, event.target);
}

/**
 * One of the shell's dialogs is over the game: the screen behind it, the game included, is
 * inert, or the focus (`focused`) is inside a dialog that is not the game's own menu. Keys
 * and gamepads then belong to the dialog.
 */
export function behindShellDialog(gameRoot: Element | null, focused: EventTarget | null): boolean {
  if (gameRoot?.closest('[inert]')) return true;
  if (!(focused instanceof Element)) return false;
  const dialog = focused.closest('[role="dialog"]');
  return dialog !== null && !(gameRoot?.contains(dialog) ?? false);
}
